/**
 * Admin tasks (`/api/admin/v2/tasks`), modelled on Meta's internal Tasks.
 * Open to every console user. A task is addressed by uuid or number (T123).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useHrApi } from '../hr/people';

const BASE = '/api/admin/v2/tasks';

export type TaskStatus = 'open' | 'in_progress' | 'in_review' | 'blocked' | 'done' | 'wontfix';
export type TaskPriority = 'ubn' | 'high' | 'mid' | 'low' | 'wishlist' | 'none';
export type TaskSort = 'priority' | 'manual' | 'updated' | 'created' | 'due' | 'start';

export interface GithubIssue {
  ref: string; // Org/repo#n
  title?: string;
  state?: 'open' | 'closed';
  url?: string;
  closedAt?: string | null;
  error?: string;
}

/** Datetimes are ISO UTC strings; dates are YYYY-MM-DD. */
export interface Task {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  projectId: string | null;
  sectionId: string | null;
  position: number;
  ownerUid: number | null;
  startDate: string | null;
  dueDate: string | null;
  progress: number | null;
  estimateHours: number | null;
  isMilestone: boolean;
  githubIssues: GithubIssue[];
  source: string;
  sourceRef: string;
  hits: number;
  lastSeenAt: string | null;
  closedAt: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  dependsOn: string[]; // task uuids this one is blocked by
  starred: boolean;
  unread: boolean;
}

export interface TaskBrief {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  ownerUid: number | null;
  dueDate: string | null;
}

export interface TaskComment {
  id: number;
  authorUid: number | null; // null = system
  kind: 'comment' | 'event'; // event body is a verb phrase: "<actor> claimed the task"
  body: string;
  editedAt: string | null;
  createdAt: string;
  likes: number[];
  likedByMe: boolean;
}

export interface TaskDetail extends Task {
  body: string;
  subscribers: number[];
  blockedBy: TaskBrief[];
  blocking: TaskBrief[];
  seenBy: number[]; // saw the latest update
  comments: TaskComment[];
}

export interface TaskPerson {
  uid: number;
  name: string;
  title: string;
}

export interface TaskSection {
  id: string;
  name: string;
  position: number;
}

export interface TaskProject {
  id: string;
  slug: string | null;
  name: string;
  parentId: string | null;
  color: string | null;
  archived: boolean;
  openCount: number;
  sections: TaskSection[];
}

/** List filters; also the shape a saved search stores. */
export interface TaskQuery {
  q?: string;
  project?: string;
  section?: string;
  owner?: string; // me | none | uid[,uid]
  creator?: string;
  subscriber?: string;
  starred?: string;
  tag?: string; // comma list
  status?: string; // open | closed | all | s1,s2
  priority?: string; // comma list
  sort?: TaskSort;
  github?: string;
}

export interface TaskSearch {
  id: string; // builtin id (todo|owner|creator|subscriber) or saved uuid
  name: string;
  query: TaskQuery;
  count: number;
  saved?: boolean;
}

export interface TaskMeta {
  statuses: TaskStatus[];
  closed: TaskStatus[];
  priorities: TaskPriority[];
  progressSteps: number[];
  people: TaskPerson[];
  me: number | null;
  projects: TaskProject[];
  searches: TaskSearch[];
  unfiledOpen: number;
}

export interface OwnerLoad {
  uid: number | null;
  open: number;
  inProgress: number;
  blocked: number;
  overdue: number;
  doneLast7d: number;
  openHours: number;
  doneLast30d: number;
  medianCycleDays: number | null;
  onTimeRate: number | null; // % of done tasks with a target date that closed by it
  oldestOpenDays: number | null;
}

export interface TaskPerformance {
  weekly: { weekStart: string; created: number; done: number }[]; // 12 weeks, oldest first
  doneLast30d: number;
  cycleDays: { median: number | null; average: number | null; count: number }; // filed -> done
  onTime: { withTarget: number; onTime: number; rate: number | null };
  oldestOpenDays: number | null;
}

export interface TaskOverview {
  total: number;
  open: number;
  closed: number;
  unowned: number;
  overdue: number;
  openHours: number;
  avgProgress: number | null;
  byStatus: Record<TaskStatus, number>;
  byPriority: Record<TaskPriority, number>;
  byOwner: OwnerLoad[];
  bySection: { sectionId: string | null; open: number; closed: number }[];
  overdueTasks: Task[];
  milestones: Task[];
  recentDone: Task[];
  performance: TaskPerformance;
}

/** Writable task fields. List fields (tags, subscribers, dependsOn, githubIssues) replace. */
export type TaskInput = Partial<
  Pick<
    Task,
    | 'title'
    | 'status'
    | 'priority'
    | 'projectId'
    | 'sectionId'
    | 'ownerUid'
    | 'startDate'
    | 'dueDate'
    | 'progress'
    | 'estimateHours'
    | 'isMilestone'
    | 'tags'
  >
> & { body?: string; subscribers?: number[]; dependsOn?: string[]; githubIssues?: string[] };

export const taskKeys = {
  all: ['admin-tasks'] as const,
  meta: () => [...taskKeys.all, 'meta'] as const,
  list: (q: TaskQuery) => [...taskKeys.all, 'list', q] as const,
  detail: (id: string) => [...taskKeys.all, 'detail', id] as const,
  overview: (project?: string, owner?: string) => [...taskKeys.all, 'overview', project ?? '', owner ?? ''] as const,
};

const qs = (q: TaskQuery) => {
  const p = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => v !== undefined && v !== '' && p.set(k, String(v)));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const useTaskMetaQuery = () => {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: taskKeys.meta(),
    queryFn: () => api<TaskMeta>('/meta'),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useTasksQuery = (q: TaskQuery) => {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: taskKeys.list(q),
    queryFn: async () => (await api<{ items: Task[] }>(qs(q))).items ?? [],
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useTaskQuery = (id: string | undefined) => {
  const api = useHrApi(BASE);
  const qc = useQueryClient();
  return useQuery({
    queryKey: taskKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const task = await api<TaskDetail>(`/${encodeURIComponent(id!)}`);
      // Opening a task marks it seen: drop the bold in lists without refetching them.
      qc.setQueriesData<Task[]>({ queryKey: [...taskKeys.all, 'list'] }, (rows) =>
        rows?.map((t) => (t.id === task.id ? { ...t, unread: false } : t)),
      );
      return task;
    },
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** `owner` (me | none | uid) narrows the overview to one person's load and delivery. */
export const useTaskOverviewQuery = (project?: string, owner?: string) => {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: taskKeys.overview(project, owner),
    queryFn: () => api<TaskOverview>(`/overview${qs({ project, owner })}`),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

function useTaskMutation<TIn, TOut>(fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TIn) => fn(api, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}

const enc = encodeURIComponent;

export const useCreateTaskMutation = () => useTaskMutation((api, body: TaskInput) => api<Task>('', 'POST', body));

export const useUpdateTaskMutation = () =>
  useTaskMutation((api, { id, ...body }: TaskInput & { id: string }) => api<Task>(`/${enc(id)}`, 'PATCH', body));

/** Drag and drop: into a section (and project) at a position. */
export const useMoveTaskMutation = () =>
  useTaskMutation(
    (api, { id, ...body }: { id: string; projectId?: string | null; sectionId?: string | null; position?: number }) =>
      api<Task>(`/${enc(id)}/move`, 'POST', body),
  );

export const useStarTaskMutation = () =>
  useTaskMutation((api, { id, starred }: { id: string; starred: boolean }) =>
    api<{ id: string; starred: boolean }>(`/${enc(id)}/star`, 'POST', { starred }),
  );

export const useCommentTaskMutation = () =>
  useTaskMutation((api, { id, body }: { id: string; body: string }) =>
    api<{ id: string; comment: TaskComment }>(`/${enc(id)}/comments`, 'POST', { body }),
  );

export const useEditCommentMutation = () =>
  useTaskMutation((api, { id, commentId, body }: { id: string; commentId: number; body: string }) =>
    api(`/${enc(id)}/comments/${commentId}`, 'PATCH', { body }),
  );

export const useDeleteCommentMutation = () =>
  useTaskMutation((api, { id, commentId }: { id: string; commentId: number }) =>
    api(`/${enc(id)}/comments/${commentId}`, 'DELETE'),
  );

export const useLikeCommentMutation = () =>
  useTaskMutation((api, { id, commentId, liked }: { id: string; commentId: number; liked: boolean }) =>
    api(`/${enc(id)}/comments/${commentId}/like`, 'POST', { liked }),
  );

export const useGithubSyncMutation = () =>
  useTaskMutation((api, _: void) =>
    api<{ tasks: number; issues: number; closed: number; errors: string[] }>('/github/sync', 'POST'),
  );

export const useCreateProjectMutation = () =>
  useTaskMutation((api, body: { name: string; parentId?: string | null }) =>
    api<TaskProject>('/projects', 'POST', body),
  );

export const useUpdateProjectMutation = () =>
  useTaskMutation((api, { id, ...body }: { id: string; name?: string; archived?: boolean }) =>
    api<TaskProject>(`/projects/${enc(id)}`, 'PATCH', body),
  );

export const useCreateSectionMutation = () =>
  useTaskMutation((api, { projectId, name }: { projectId: string; name: string }) =>
    api<TaskSection>(`/projects/${enc(projectId)}/sections`, 'POST', { name }),
  );

export const useUpdateSectionMutation = () =>
  useTaskMutation((api, { id, ...body }: { id: string; name?: string; position?: number }) =>
    api<TaskSection>(`/sections/${enc(id)}`, 'PATCH', body),
  );

export const useDeleteSectionMutation = () =>
  useTaskMutation((api, id: string) => api(`/sections/${enc(id)}`, 'DELETE'));

export const useSaveSearchMutation = () =>
  useTaskMutation((api, body: { name: string; query: TaskQuery }) => api<TaskSearch>('/searches', 'POST', body));

export const useDeleteSearchMutation = () =>
  useTaskMutation((api, id: string) => api(`/searches/${enc(id)}`, 'DELETE'));
